import { useCallback, useEffect, useRef, useState } from 'react'

// UI STATE CHANGES
export const useOptionsState = () => {
  const [selectStates, setSelectStates] = useState({})
  const dropdownRefs = useRef({})

  const toggleDropdown = useCallback((fieldId) => {
    setSelectStates((prevStates) => ({
      ...prevStates,
      [fieldId]: {
        ...prevStates[fieldId],
        isOpen: !prevStates[fieldId]?.isOpen
      }
    }))
  }, [])

  // Close all open dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      setSelectStates((prevStates) => {
        const updates = {}
        let changed = false
        for (const id of Object.keys(prevStates)) {
          if (!prevStates[id]?.isOpen) continue
          const ref = dropdownRefs.current[id]
          if (ref && !ref.contains(e.target)) {
            updates[id] = { ...prevStates[id], isOpen: false }
            changed = true
          }
        }
        return changed ? { ...prevStates, ...updates } : prevStates
      })
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleOptionClick = (fieldId, optionValue) => {
    setSelectStates((prevStates) => ({
      ...prevStates,
      [fieldId]: {
        ...prevStates[fieldId],
        selectedOption: optionValue,
        isOpen: false
      }
    }))
  }

  const resetSelectStates = useCallback(() => {
    setSelectStates((prevStates) => {
      const nextStates = {}

      for (const [fieldId, state] of Object.entries(prevStates)) {
        nextStates[fieldId] = {
          ...state,
          selectedOption: '',
          isOpen: false
        }
      }

      return nextStates
    })
  }, [])

  //Handle file upload changes

  const handleFileChange = (index, newValue, id, fieldValuesArray) => {
    function checkIfNumberExists(fieldValuesArray, id) {
      for (const pair of fieldValuesArray) {
        if (pair.id === id) {
          pair['fileUploadValues'] = [newValue] // Ensure the value is an array of files
          return true
        }
      }
      return false
    }

    // Check if the field ID already exists in the array, update if exists
    if (!checkIfNumberExists(fieldValuesArray, id)) {
      // If it doesn't exist, add a new entry
      fieldValuesArray.push({
        id: id,
        fileUploadValues: [newValue] // Add the file as an array of files
      })
    }
  }

  return {
    handleFileChange,
    selectStates,
    handleOptionClick,
    resetSelectStates,
    toggleDropdown,
    dropdownRefs
  }
}

// FIELD VALUES ARRAY CHANGES
export const useFieldValues = (formFields) => {
  const [fieldValuesArray] = useState([])
  // Initialize field values from formFields
  const [fieldValues, setFieldValues] = useState(() => {
    const initialFieldValues = {}
    formFields.nodes.forEach((field) => {
      initialFieldValues[field.databaseId] = field.value || ''
    })
    return initialFieldValues
  })
  // Function to update the field value in the state
  const updateFieldValue = (fieldId, value) => {
    setFieldValues((prevFieldValues) => ({
      ...prevFieldValues,
      [fieldId]: value
    }))
  }

  const clearForm = () => {
    const initialFieldValues = {}
    formFields.nodes.forEach((field) => {
      initialFieldValues[field.databaseId] = field.value || ''
    })
    setFieldValues(initialFieldValues)
  }

  return {
    fieldValuesArray,
    fieldValues,
    setFieldValues,
    clearForm,
    updateFieldValue
  }
}

// HANDLE GF FIELD UPDATES - GF helpers that place form values into needed shape for GraphQLforGF plugin mutation and form submission

// Handle change to simple GF fields
export const handleInputChange = (index, newValue, id, fieldValuesArray) => {
  function checkIfNumberExists(fieldValuesArray, id) {
    for (const pair of fieldValuesArray) {
      if (pair.id === id) {
        pair.value = newValue
        return true
      }
    }
    return false
  }

  if (!checkIfNumberExists(fieldValuesArray, id)) {
    fieldValuesArray.push({ id: id, value: newValue })
  }
}

// Handle change to GF Checkbox field
export const handleSubfieldsInputChange = (index, newValue, fieldID, key, field, fieldValuesArray) => {
  function checkIfNumberExists(fieldValuesArray, fieldID, key, field) {
    let changedValueName = field.toLowerCase() + 'Values'
    for (const pair of fieldValuesArray) {
      if (pair.id === fieldID) {
        // If the nameValues is not an object, initialize it as a new object with the key-value pair
        if (typeof pair[changedValueName] !== 'object') {
          pair[changedValueName] = { [key]: newValue }
        } else {
          // Update the value corresponding to the key
          pair[changedValueName][key] = newValue
        }
        return true
      }
    }
    return false
  }

  if (!checkIfNumberExists(fieldValuesArray, fieldID, key, field)) {
    let changedValueName = field.toLowerCase() + 'Values'
    fieldValuesArray.push({
      id: fieldID,
      [changedValueName]: { [key]: newValue }
    })
  }
}

// Handle GF field CAPTCHA field
export const handleCaptcha = (id, value, fieldValuesArray) => {
  function checkIfNumberExists(fieldValuesArray, id) {
    for (const pair of fieldValuesArray) {
      if (pair.id === id) {
        pair.value = value
        return true
      }
    }
    return false
  }

  if (!checkIfNumberExists(fieldValuesArray, id)) {
    fieldValuesArray.push({ id: id, value: value })
  }
}
